import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import TokenCard from '../TokenCard';
import OrderDisplay from '../orders/OrderDisplay';
import { Button } from '@vaiu/ui/button';
import { ChevronDown, ChevronUp, Package } from 'lucide-react';
import { 
  isTokenTransaction, 
  createTokenDisplayData, 
  getTokenDisplayOptions, 
  getTokenStatusText 
} from '@/utils/tokenUtils';

interface TokenRendererProps {
  transaction: any;
  showTimestamp?: boolean;
  className?: string;
  onClick?: () => void;
}

/**
 * Shared component for consistent token rendering across the application
 * Used by TransactionDetails, TransactionHistoryFiltered, and other components
 */
const TokenRenderer: React.FC<TokenRendererProps> = ({ 
  transaction, 
  showTimestamp = true,
  className = '',
  onClick 
}) => {
  const [showOrder, setShowOrder] = useState(false);
  
  // Only render if this is actually a token transaction
  if (!isTokenTransaction(transaction)) {
    return null;
  }

  // Check if token has an order in payload
  const order = transaction.payload?.order || 
                 transaction.data?.payload?.order ||
                 transaction.rawData?.payload?.order;
  const hasOrder = !!order;
  const isInvoice = Boolean(transaction.isInvoice || transaction.tokenType === 'invoice');
  const cardClickable = Boolean(onClick) && !hasOrder && !isInvoice;

  const displayOptions = getTokenDisplayOptions(transaction);
  const statusText = getTokenStatusText(transaction);

  return (
    <div className={className}>
      <div 
        className={`${cardClickable ? 'cursor-pointer' : ''}`} 
        onClick={cardClickable ? onClick : undefined}
      >
        <TokenCard 
          rawBackendData={transaction} // Use new rawBackendData prop
          theme={displayOptions.theme}
          showActions={displayOptions.showActions}
          showQRCode={displayOptions.showQRCode}
          qrMode="button"
          className={displayOptions.className}
          borderless={true} // Remove extra card wrapper for nested contexts
        />
        
        {/* Order toggle button */}
        {hasOrder && (
          <div className="px-4 pb-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowOrder(!showOrder);
              }}
              className="w-full flex items-center justify-center gap-2"
            >
              <Package className="w-4 h-4" />
              {showOrder ? 'Hide Order Details' : 'View Order Details'}
              {showOrder ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        )}
        
        {showTimestamp && (
          <div className="mt-1 text-xs text-gray-500 px-1">
            {statusText} {formatDistanceToNow(new Date(transaction.date || transaction.createdAt), { addSuffix: true })}
          </div>
        )}
      </div>
      
      {/* Order Display */}
      {hasOrder && showOrder && (
        <div className="mt-4 px-4">
          <OrderDisplay 
            order={order}
            className="border-l-4 border-l-blue-500"
            showHeader={false}
          />
        </div>
      )}
    </div>
  );
};

export default TokenRenderer; 